import { useCallback, useRef, useState } from "react";

import { useSupabase } from "@/auth/SupabaseProvider";

/**
 * Streaming hook for the in-app "Report an issue" chat (issue #287).
 *
 * Wire shape — see `services/orchestrator/src/routes/admin-report-issue.ts`
 * for the source of truth.
 *
 * Unlike `use-run-stream.ts` (which uses `EventSource` + a ticket-fetch
 * round-trip for the live-run view), this hook uses `fetch` so we can
 *
 *  1. POST a body (`messages[]` transcript) — `EventSource` is GET-only.
 *  2. Attach the Supabase JWT directly in `Authorization: Bearer …`
 *     instead of minting a stream ticket; the endpoint sits inside the
 *     JWT-gated admin scope.
 *
 * The hook owns the full conversation locally — every send re-POSTs the
 * full message history. No DB-backed session on the server side; the
 * `sessionId` we generate on first send only scopes the audit / LLM-usage
 * rows for forensics.
 *
 * State machine:
 *   idle → streaming → (filed | needs_input | errored)
 *     filed → cannot re-send; user must reset()
 *     errored → can re-send (e.g. transient upstream blip)
 *     needs_input → can re-send (the model asked a clarifying question)
 */

const API_BASE_URL: string = import.meta.env.VITE_API_BASE_URL ?? "";

export interface ReportIssueChatMessage {
  role: "user" | "assistant";
  /**
   * Rendered text. The wire shape carries content as
   * `Array<{type:"text",text:string}>` — we flatten on receive and re-
   * expand on send so the UI doesn't have to think about content blocks.
   */
  text: string;
}

export interface FiledIssue {
  number: number;
  htmlUrl: string;
  title: string;
}

export type ReportIssueState =
  | "idle"
  | "streaming"
  | "needs_input"
  | "filed"
  | "errored";

export interface ReportIssueError {
  /**
   * Closed-list error code from the orchestrator's SSE wire. Surfaced
   * here so the UI can switch on it for retry vs contact-admin
   * affordances.
   */
  code: string;
  message: string;
}

export interface UseReportIssueStreamResult {
  /**
   * The full transcript so far. User bubbles + assistant bubbles in
   * order. The streaming-in-progress bubble is included with its
   * partial text.
   */
  messages: ReportIssueChatMessage[];
  /**
   * `idle` before the first send. `streaming` while a stream is open
   * (the last `assistant` message in `messages` is being filled
   * incrementally). `needs_input` after a clarifying-question turn —
   * UI shows the input as enabled. `filed` after a successful filing —
   * UI shows the link + a "Start over" button. `errored` on any
   * terminal error — UI shows the error banner with a retry button.
   */
  state: ReportIssueState;
  /**
   * Populated on `filed`. Surfaced to the UI as a clickable link.
   */
  filedIssue: FiledIssue | null;
  /**
   * Populated on `errored`. UI renders {@link ReportIssueError.message}
   * verbatim.
   */
  error: ReportIssueError | null;
  /**
   * Send a new user turn. Appends it to the local history and opens
   * an SSE stream; the assistant reply streams into the last bubble.
   * Calls beyond the {@link ReportIssueState} `filed` state are
   * rejected silently (the UI hides the input then).
   */
  send: (userText: string) => void;
  /**
   * Reset the conversation to `idle` with an empty transcript and a
   * fresh `sessionId`. Used by the "Start over" button on the filed
   * screen and the "Try again" button on the errored screen.
   */
  reset: () => void;
}

/**
 * Generate a new chat-session UUID. Re-rolled on every `reset()`.
 * `crypto.randomUUID` is available in every browser the admin UI
 * supports.
 */
function newSessionId(): string {
  return crypto.randomUUID();
}

export function useReportIssueStream(): UseReportIssueStreamResult {
  const { client } = useSupabase();
  const [messages, setMessages] = useState<ReportIssueChatMessage[]>([]);
  const [state, setState] = useState<ReportIssueState>("idle");
  const [filedIssue, setFiledIssue] = useState<FiledIssue | null>(null);
  const [error, setError] = useState<ReportIssueError | null>(null);
  const sessionIdRef = useRef<string>(newSessionId());
  const abortRef = useRef<AbortController | null>(null);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    sessionIdRef.current = newSessionId();
    setMessages([]);
    setState("idle");
    setFiledIssue(null);
    setError(null);
  }, []);

  const send = useCallback(
    (userText: string) => {
      // Cannot send after a successful filing — the UI hides the input
      // in that state, but the guard here is defensive in case a queued
      // keystroke fires after the state flip.
      if (state === "filed" || state === "streaming") return;
      const trimmed = userText.trim();
      if (trimmed.length === 0) return;

      // Append the user turn locally so the bubble renders immediately.
      // Append an empty assistant placeholder that will fill via
      // text_delta chunks.
      setMessages((prev) => [
        ...prev,
        { role: "user", text: trimmed },
        { role: "assistant", text: "" },
      ]);
      setState("streaming");
      setError(null);

      // Drive the stream in a closure so we can `await` inside without
      // making the React callback itself async.
      void (async () => {
        const controller = new AbortController();
        abortRef.current = controller;
        try {
          const {
            data: { session },
          } = await client.auth.getSession();
          if (!session) {
            throw new Error("no_session");
          }

          // Body matches `sendBodySchema` server-side: sessionId +
          // full message history. We re-expand the flat text into the
          // wire's content-block shape.
          const wireMessages = [
            ...messages.map((m) => ({
              role: m.role,
              content: [{ type: "text", text: m.text }],
            })),
            { role: "user", content: [{ type: "text", text: trimmed }] },
          ];

          const url = new URL(
            "/api/admin/report-issue/messages",
            API_BASE_URL || window.location.origin,
          );

          const response = await fetch(url.toString(), {
            method: "POST",
            headers: {
              Authorization: `Bearer ${session.access_token}`,
              "Content-Type": "application/json",
              Accept: "text/event-stream",
            },
            body: JSON.stringify({
              sessionId: sessionIdRef.current,
              messages: wireMessages,
            }),
            signal: controller.signal,
          });

          if (!response.ok || !response.body) {
            // Try to read a JSON error body — both 4xx (turn cap,
            // quota, invalid) and the rare 5xx land here.
            let body: unknown = null;
            try {
              body = await response.json();
            } catch {
              /* ignore */
            }
            const code =
              typeof (body as { error?: unknown })?.error === "string"
                ? (body as { error: string }).error
                : `http_${response.status}`;
            throw new ReportIssueWireError(code, mapHttpErrorMessage(code));
          }

          await consumeSseStream(response.body, {
            onTextDelta: (text) => {
              setMessages((prev) => {
                const last = prev[prev.length - 1];
                if (!last || last.role !== "assistant") return prev;
                const updated = { ...last, text: last.text + text };
                return [...prev.slice(0, -1), updated];
              });
            },
            onIssueFiled: (filed) => {
              setFiledIssue(filed);
            },
            onMessageStop: ({ filed }) => {
              setState(filed ? "filed" : "needs_input");
            },
            onError: (err) => {
              throw new ReportIssueWireError(err.code, err.message);
            },
          });
        } catch (err) {
          if (controller.signal.aborted) {
            // Caller hit reset() mid-stream — state will be set by
            // reset() itself; do nothing here.
            return;
          }
          if (err instanceof ReportIssueWireError) {
            setError({ code: err.code, message: err.message });
          } else {
            setError({
              code: "internal",
              message: "Something went wrong. Please try again.",
            });
          }
          setState("errored");
          // Strip the empty-assistant placeholder we appended at send
          // time so the UI doesn't render an empty bubble next to the
          // error banner.
          setMessages((prev) => {
            const last = prev[prev.length - 1];
            if (last && last.role === "assistant" && last.text === "") {
              return prev.slice(0, -1);
            }
            return prev;
          });
        } finally {
          abortRef.current = null;
        }
      })();
    },
    [client, messages, state],
  );

  return { messages, state, filedIssue, error, send, reset };
}

// ─── SSE parsing ────────────────────────────────────────────────────────

interface SseCallbacks {
  onTextDelta: (text: string) => void;
  onIssueFiled: (filed: FiledIssue) => void;
  onMessageStop: (payload: { filed: boolean }) => void;
  onError: (payload: { code: string; message: string }) => void;
}

/**
 * Consume an SSE response body until end. Splits on the standard
 * `\n\n` event boundary and dispatches by `event:` field. Browsers'
 * native `EventSource` would do this for us but only over GET — for
 * the POST-bodied stream we have to do it manually.
 */
async function consumeSseStream(
  body: ReadableStream<Uint8Array>,
  callbacks: SseCallbacks,
): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder("utf-8");
  let buffer = "";

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    // SSE events are separated by a blank line (\n\n). Process all
    // complete events in the buffer; keep the trailing fragment.
    let boundary = buffer.indexOf("\n\n");
    while (boundary !== -1) {
      const rawEvent = buffer.slice(0, boundary);
      buffer = buffer.slice(boundary + 2);
      dispatchSseEvent(rawEvent, callbacks);
      boundary = buffer.indexOf("\n\n");
    }
  }
}

function dispatchSseEvent(rawEvent: string, callbacks: SseCallbacks): void {
  let eventName = "message";
  let dataLines: string[] = [];
  for (const line of rawEvent.split("\n")) {
    if (line.startsWith(":")) continue; // SSE comment (keep-alive)
    if (line.startsWith("event:")) {
      eventName = line.slice(6).trim();
    } else if (line.startsWith("data:")) {
      dataLines.push(line.slice(5).trimStart());
    }
  }
  if (dataLines.length === 0) return;
  let data: unknown;
  try {
    data = JSON.parse(dataLines.join("\n"));
  } catch {
    return;
  }

  switch (eventName) {
    case "text_delta": {
      const text = (data as { text?: unknown }).text;
      if (typeof text === "string") callbacks.onTextDelta(text);
      return;
    }
    case "issue_filed": {
      const d = data as { number?: unknown; htmlUrl?: unknown; title?: unknown };
      if (
        typeof d.number === "number" &&
        typeof d.htmlUrl === "string" &&
        typeof d.title === "string"
      ) {
        callbacks.onIssueFiled({
          number: d.number,
          htmlUrl: d.htmlUrl,
          title: d.title,
        });
      }
      return;
    }
    case "message_stop": {
      const d = data as { filed?: unknown };
      callbacks.onMessageStop({ filed: d.filed === true });
      return;
    }
    case "error": {
      const d = data as { code?: unknown; message?: unknown };
      callbacks.onError({
        code: typeof d.code === "string" ? d.code : "internal",
        message:
          typeof d.message === "string"
            ? d.message
            : "Something went wrong. Please try again.",
      });
      return;
    }
    default:
      // tool_use_start, tool_use_stop — present on the wire but the
      // current UI doesn't render them as distinct affordances. Add a
      // case here if a future iteration wants a "filing…" spinner.
      return;
  }
}

class ReportIssueWireError extends Error {
  readonly code: string;
  constructor(code: string, message: string) {
    super(message);
    this.code = code;
    this.name = "ReportIssueWireError";
  }
}

/**
 * Map the orchestrator's HTTP-error code (returned as JSON `{error}`
 * on 4xx/5xx, distinct from the SSE `error` event's `code`) to a
 * user-facing message.
 */
function mapHttpErrorMessage(code: string): string {
  switch (code) {
    case "quota_exceeded":
      return "You've filed the maximum issues for today. Please try again tomorrow.";
    case "turn_cap_exceeded":
      return "This conversation has gone on too long. Please start over.";
    case "sse_required":
      return "Your browser cannot receive the streaming response.";
    case "invalid_body":
      return "The request was malformed. Please try again.";
    case "unauthorized":
      return "You need to sign in again.";
    case "unsupported_media_type":
      return "Internal error sending the request.";
    default:
      return "Something went wrong. Please try again.";
  }
}
